import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaUsers, FaRocket, FaHeart, FaAward, FaLightbulb, FaShieldAlt, FaChartLine, FaClock, FaHandshake, FaTools, FaStar, FaWhatsapp, FaCode, FaDatabase, FaServer, FaMobileAlt, FaBrain, FaCog } from 'react-icons/fa';
import { pageSeoData, seoImage } from '../config/pageSeoConfig';
import { companyInfo, organizationSchema } from '../config/seoConfig';
import './FullStackExpert.scss';

const AppsDevelopmentCompany: React.FC = () => {
  // Typewriter effect for code
  const [codeLine1, setCodeLine1] = useState('');
  const [codeLine2, setCodeLine2] = useState('');
  const [codeLine3, setCodeLine3] = useState('');
  const [showCursor1, setShowCursor1] = useState(true);
  const [showCursor2, setShowCursor2] = useState(false);
  const [showCursor3, setShowCursor3] = useState(false);

  const fullCodeLine1 = 'const buildApp = (ai, stack) => {';
  const fullCodeLine2 = '  return ai.integrate(stack);';
  const fullCodeLine3 = '}; // ✨ Innovation';

  useEffect(() => {
    document.title = pageSeoData.fullstackexpert.title;
  }, []);

  // Typewriter animation effect
  useEffect(() => {
    let timeout1: NodeJS.Timeout;
    let timeout2: NodeJS.Timeout;
    let timeout3: NodeJS.Timeout;
    let resetTimeout: NodeJS.Timeout;

    const typeCodeLine1 = (index: number) => {
      if (index <= fullCodeLine1.length) {
        setCodeLine1(fullCodeLine1.substring(0, index));
        timeout1 = setTimeout(() => typeCodeLine1(index + 1), 50);
      } else {
        setShowCursor1(false);
        setShowCursor2(true);
        typeCodeLine2(0);
      }
    };

    const typeCodeLine2 = (index: number) => {
      if (index <= fullCodeLine2.length) {
        setCodeLine2(fullCodeLine2.substring(0, index));
        timeout2 = setTimeout(() => typeCodeLine2(index + 1), 50);
      } else {
        setShowCursor2(false);
        setShowCursor3(true);
        typeCodeLine3(0);
      }
    };

    const typeCodeLine3 = (index: number) => {
      if (index <= fullCodeLine3.length) {
        setCodeLine3(fullCodeLine3.substring(0, index));
        timeout3 = setTimeout(() => typeCodeLine3(index + 1), 50);
      } else {
        // Wait 2 seconds then reset and restart
        resetTimeout = setTimeout(() => {
          setCodeLine1('');
          setCodeLine2('');
          setCodeLine3('');
          setShowCursor1(true);
          setShowCursor2(false);
          setShowCursor3(false);
          typeCodeLine1(0);
        }, 3000);
      }
    };

    // Start the animation
    typeCodeLine1(0);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(resetTimeout);
    };
  }, []);

  const seoData = pageSeoData.fullstackexpert;

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      organizationSchema,
      {
        "@type": "AboutPage",
        "@id": `${seoData.url}#webpage`,
        "url": seoData.url,
        "name": seoData.title,
        "description": seoData.description,
        "isPartOf": {
          "@id": `${companyInfo.url}#website`
        },
        "about": {
          "@id": `${companyInfo.url}#organization`
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "url": seoImage
        },
        "inLanguage": "en-US"
      }
    ]
  };

  const stats = [
    { number: "150+", label: "Projects Completed", description: "Successful web development projects delivered" },
    { number: "98%", label: "Client Satisfaction", description: "Client retention rate and satisfaction score" },
    { number: "10+", label: "Years Experience", description: "Years of professional web development experience" },
    { number: "24/7", label: "Support Available", description: "Round-the-clock technical support and maintenance" }
  ];

  const whyChooseUs = [
    {
      icon: FaChartLine,
      title: "Proven Track Record",
      description: "Over 150 successful web development projects with measurable results and ROI improvement for our clients."
    },
    {
      icon: FaClock,
      title: "Fast Delivery",
      description: "Agile development methodology ensures rapid project delivery without compromising on quality or functionality."
    },
    {
      icon: FaHandshake,
      title: "Dedicated Support",
      description: "24/7 technical support, ongoing maintenance, and continuous optimization for your web applications."
    },
    {
      icon: FaTools,
      title: "Latest Technology",
      description: "Cutting-edge web technologies, modern frameworks, and best practices for scalable solutions."
    },
    {
      icon: FaShieldAlt,
      title: "Security First",
      description: "Advanced security measures, data encryption, and compliance with industry standards to protect your business."
    },
    {
      icon: FaUsers,
      title: "Transparent Communication",
      description: "Regular updates, clear project timelines, and collaborative approach to keep you informed every step of the way."
    },
    {
      icon: FaRocket,
      title: "Scalable Solutions",
      description: "Build applications that grow with your business, handling increased traffic and complex requirements seamlessly."
    },
    {
      icon: FaAward,
      title: "Quality Assurance",
      description: "Rigorous testing processes, automated QA, and performance optimization ensuring bug-free, high-quality deliverables."
    }
  ];

  const process = [
    {
      step: "01",
      title: "Discovery & Planning",
      description: "Comprehensive project analysis, requirement gathering, and strategic planning for optimal results."
    },
    {
      step: "02", 
      title: "Design & Development",
      description: "User-centered design creation and robust development using modern web technologies and frameworks."
    },
    {
      step: "03",
      title: "Testing & Launch",
      description: "Rigorous quality assurance testing, performance optimization, and seamless deployment to production."
    },
    {
      step: "04",
      title: "Support & Maintenance",
      description: "Ongoing technical support, regular updates, security monitoring, and continuous optimization."
    }
  ];

  const testimonials = [
    {
      name: "Michael Rodriguez",
      company: "TechStart Inc.",
      role: "CEO",
      content: "Outstanding web development service! They delivered our e-commerce platform ahead of schedule with exceptional quality. Highly recommended for any business looking for professional web solutions.",
      rating: 5
    },
    {
      name: "Sarah Thompson",
      company: "Digital Marketing Pro",
      role: "Marketing Director", 
      content: "The team's expertise in modern web technologies is impressive. Our website performance improved by 300% after their optimization. Best web development company we've worked with.",
      rating: 5
    },
    {
      name: "David Kim",
      company: "StartupHub",
      role: "Founder",
      content: "Professional, reliable, and results-driven. They transformed our outdated website into a modern, mobile-responsive platform that significantly boosted our online presence.",
      rating: 5
    }
  ];

  const team = [
    {
      name: "Vishnu Charan",
      role: "Founder & Lead Developer",
      image: "/images/app-developer-vishnu-charan.jpg",
      bio: "full stack developer with 10+ years of experience in modern & high performance web technologies."
    },
    {
      name: "Ram Naresh",
      role: "Business Developer",
      image: "/images/business-developer-ram-naresh.jpg",
      bio: "17+ years of experience in business development with a focus on building strong client relationships and driving growth."
    },
   
    {
      name: "Manikant Mathur",
      role: "Business Developer",
      image: "/images/business-developer-manikant-mathur.jpg",
      bio: "14+ years of experience in business development and client relationship management."
    },
    {
      name: "Devendra Rajput",
      role: "Business Developer",
      image: "/images/Automation-developer-devendra-rajput.jpg",
      bio: "12+ years of experience in automation development with expertise in CI/CD pipelines and test automation."
    },
    
    {
      name: "Kamlesh Kumar",
      role: "Digital Marketing Expert",
      image: "/images/digital-marketing-kamlesh-kumar.jpg",
      bio: "12+ years of experience in digital marketing with a focus on SEO and content strategy."
    },
    
    
  ];
  

  // SEO constants
  const seoTitle = seoData.title;
  const seoDescription = seoData.description;
  const seoKeywords = seoData.keywords;
  const seoUrl = seoData.url;

  const values = [
    {
      icon: FaHeart,
      title: "Passion",
      description: "We love what we do and it shows in every project we deliver."
    },
    {
      icon: FaRocket,
      title: "Innovation",
      description: "Always exploring new technologies and creative solutions."
    },
    {
      icon: FaUsers,
      title: "Collaboration",
      description: "Working closely with clients to achieve their vision."
    },
    {
      icon: FaAward,
      title: "Excellence",
      description: "Committed to delivering high-quality results every time."
    },
    {
      icon: FaLightbulb,
      title: "Creativity",
      description: "Bringing fresh ideas and innovative approaches to every challenge."
    },
    {
      icon: FaShieldAlt,
      title: "Security",
      description: "Ensuring robust security measures and data protection in all our solutions."
    }
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:site_name" content="App Developer Pro" />
        <meta property="og:image" content={seoImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="full stack ai App Developer Pro" />
        <meta property="og:locale" content="en_US" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@appdeveloperpro" />
        <meta name="twitter:creator" content="@appdeveloperpro" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:image:alt" content="full stack ai App Developer Pro" />
        {/* JSON-LD Schema */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="who-we-are">
      <section className="who-we-are__hero">
        <div className="who-we-are__hero-content">
          <motion.div
            className="who-we-are__hero-text"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Full Stack Expert - <br/><span className="home__hero-highlight">Development Solutions Specialist </span>USA-India-Germany-UK-Spain</h1>
            <h2>Your Full Stack Development Engineer<span className="home__hero-icon">🤝</span></h2>
            <h3> React, Next.js, Node.js, Python Partner </h3>
            <p>
 As a Full Stack Expert, Specialist to develop high-performance, custom web/mobile applications & software to attract real organic traffic, boost global visibility, and convert users into loyal customers. From intelligent automation to seamless user experiences, I help you scale your business and increase sales worldwide.

            </p>
          </motion.div>
      
          <motion.div
            className="who-we-are__hero-visual"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="who-we-are__hero-interactive">
              {/* Floating Tech Icons Around Developer */}
              <motion.div 
                className="who-we-are__hero-tech-icon who-we-are__hero-tech-icon--1"
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <FaCode />
              </motion.div>
              <motion.div 
                className="who-we-are__hero-tech-icon who-we-are__hero-tech-icon--2"
                animate={{ y: [0, 15, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <FaDatabase />
              </motion.div>
              <motion.div 
                className="who-we-are__hero-tech-icon who-we-are__hero-tech-icon--3"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <FaBrain />
              </motion.div>
              <motion.div 
                className="who-we-are__hero-tech-icon who-we-are__hero-tech-icon--4"
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
              >
                <FaServer />
              </motion.div>
              <motion.div 
                className="who-we-are__hero-tech-icon who-we-are__hero-tech-icon--5"
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut" }}
              >
                <FaMobileAlt />
              </motion.div>
              <motion.div 
                className="who-we-are__hero-tech-icon who-we-are__hero-tech-icon--6"
                animate={{ y: [0, 10, 0], rotate: [0, 360] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <FaCog />
              </motion.div>
              
              {/* Central Developer Coding Scene */}
              <motion.div 
                className="who-we-are__hero-developer-scene"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                {/* Developer Illustration */}
                <div className="who-we-are__hero-developer">
                  {/* Head */}
                  <div className="who-we-are__hero-dev-head"></div>
                  {/* Body */}
                  <div className="who-we-are__hero-dev-body"></div>
                  {/* Arms */}
                  <div className="who-we-are__hero-dev-arms">
                    <div className="who-we-are__hero-dev-arm who-we-are__hero-dev-arm--left"></div>
                    <div className="who-we-are__hero-dev-arm who-we-are__hero-dev-arm--right"></div>
                  </div>
                </div>
                
                {/* Computer/Laptop */}
                <div className="who-we-are__hero-computer">
                  <div className="who-we-are__hero-screen">
                    {/* Code Lines Animation - Typewriter Effect */}
                    <div className="who-we-are__hero-code-line">
                      <span style={{ color: '#c678dd' }}>const</span> 
                      <span style={{ color: '#e06c75' }}> buildApp</span> 
                      <span style={{ color: '#abb2bf' }}> = (</span>
                      <span style={{ color: '#d19a66' }}>ai, stack</span>
                      <span style={{ color: '#abb2bf' }}>) =&gt; {'{'}</span>
                      {showCursor1 && <span className="who-we-are__hero-code-cursor">|</span>}
                    </div>
                    {codeLine1.length === fullCodeLine1.length && (
                      <div className="who-we-are__hero-code-line who-we-are__hero-code-line--short">
                        &nbsp;&nbsp;
                        <span style={{ color: '#c678dd' }}>return</span> 
                        <span style={{ color: '#e06c75' }}> ai</span>
                        <span style={{ color: '#abb2bf' }}>.</span>
                        <span style={{ color: '#61afef' }}>integrate</span>
                        <span style={{ color: '#abb2bf' }}>(</span>
                        <span style={{ color: '#e06c75' }}>stack</span>
                        <span style={{ color: '#abb2bf' }}>)</span>
                        <span style={{ color: '#abb2bf' }}>;</span>
                        {showCursor2 && <span className="who-we-are__hero-code-cursor">|</span>}
                      </div>
                    )}
                    {codeLine2.length === fullCodeLine2.length && (
                      <div className="who-we-are__hero-code-line">
                        <span style={{ color: '#abb2bf' }}>{'}'}</span>
                        <span style={{ color: '#abb2bf' }}>;</span> 
                        <span style={{ color: '#5c6370' }}> // ✨ Innovation</span>
                        {showCursor3 && <span className="who-we-are__hero-code-cursor">|</span>}
                      </div>
                    )}
                  </div>
                  <div className="who-we-are__hero-keyboard"></div>
                </div>
                
                {/* Chair */}
                <div className="who-we-are__hero-chair">
                  <div className="who-we-are__hero-chair-back"></div>
                  <div className="who-we-are__hero-chair-seat"></div>
                  <div className="who-we-are__hero-chair-leg"></div>
                </div>
              </motion.div>
              
              {/* Decorative Elements */}
              <motion.div 
                className="who-we-are__hero-circle who-we-are__hero-circle--1"
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 180, 360] 
                }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              />
              <motion.div 
                className="who-we-are__hero-circle who-we-are__hero-circle--2"
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [360, 180, 0] 
                }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="who-we-are__story">
        <div className="who-we-are__content">
           <motion.div
            className="who-we-are__image"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <img src="/images/full-stack-expert.png" alt="full stack expert" />
          </motion.div>
          <motion.div
            className="who-we-are__text"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Your Vision, Powered by AI — Full Stack Development That Thinks, Learns & Scales</h2>
            <h3>Bringing Intelligent Applications to Life with Full Stack AI Development Solutions in USA-India-Germany-UK-Spain</h3>
            <p>
              In today's AI-driven world, businesses need more than just traditional development—they need smart, adaptive, and scalable applications that deliver real value. As a Full Stack App Expert, I combine the power of artificial intelligence with full-stack development to create products that not only work flawlessly but also think, learn, and improve over time.
</p>
<p>App Developer Pro design and build complete AI-powered ecosystems—from intuitive user interfaces and high-performance APIs to advanced machine learning models and automation pipelines. The result? Intelligent applications that help you work smarter, move faster, and stay ahead of the competition.</p>
   <p>In short,Being a Apps Developer bridge technology with business goals—building ai driven products that are fast, secure, and user-centric. So What are you waiting for, app developer is here to build your self-improving applications that streamline operations, accelerate decision-making, and give your business a powerful competitive edge in a rapidly evolving digital world.</p>
           
            <div className="who-we-are__story-buttons">
              <motion.a
                href="/contact"
                className="who-we-are__story-cta"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started Now
                <FaRocket style={{ marginLeft: '10px' }} />
              </motion.a>

              <motion.a
                href="https://wa.me/918826360188?text=Hi%20Vishnu%20Charan!%20I'm%20interested%20in%20discussing%20a%20Full%20Stack%20AI%20development%20project.%20Can%20we%20talk?"
                target="_blank"
                rel="noopener noreferrer"
                className="who-we-are__story-cta who-we-are__story-cta--whatsapp"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Talk to Me ?
                <FaWhatsapp style={{ marginLeft: '10px', fontSize: '1.3rem' }} />
              </motion.a>
            </div>
            
          </motion.div>
         
        </div>
      </section>

      {/* What I Deliver Section */}
      <section className="who-we-are__deliver">
        <div className="who-we-are__deliver-container">
          <motion.h2 
            className="who-we-are__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
           As A Full Stack Expert, What App Developer Pro Deliver?
          
          </motion.h2>
          <h3> One stop Solution for web/mobile apps & custom software development</h3>
           <p>App Developer Pro provide end-to-end, intelligent digital solutions that combine the power of AI with modern full-stack engineering. From designing intuitive user experiences to building high-performance backends, smart automation pipelines, and advanced machine learning integrations, App Developer Pro delivers applications that are fast, scalable, and truly intelligent. Every product is crafted to enhance efficiency, streamline workflows, and help your business stay ahead in an AI-driven future.</p>
          <div className="who-we-are__deliver-grid">
            <motion.div
              className="who-we-are__deliver-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="who-we-are__deliver-header">
                <div className="who-we-are__deliver-icon">
                  <FaRocket />
                </div>
                <h3>AI-Driven Application Development</h3>
              </div>
              <p>Turn your idea into a smart application powered by machine learning, NLP, and automation.</p>
            </motion.div>

            <motion.div
              className="who-we-are__deliver-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="who-we-are__deliver-header">
                <div className="who-we-are__deliver-icon">
                  <FaLightbulb />
                </div>
                <h3>End-to-End Full-Stack Engineering</h3>
              </div>
              <p>From frontend UI/UX to backend architecture and cloud deployment, everything is built to scale.</p>
            </motion.div>

            <motion.div
              className="who-we-are__deliver-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="who-we-are__deliver-header">
                <div className="who-we-are__deliver-icon">
                  <FaTools />
                </div>
                <h3>Automation & Workflow Intelligence</h3>
              </div>
              <p>Reduce manual work with AI agents, predictive systems, and automated business logic.</p>
            </motion.div>

            <motion.div
              className="who-we-are__deliver-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="who-we-are__deliver-header">
                <div className="who-we-are__deliver-icon">
                  <FaChartLine />
                </div>
                <h3>High-Performance APIs & Microservices</h3>
              </div>
              <p>Fast, secure, and scalable APIs designed to integrate seamlessly with your application.</p>
            </motion.div>

            <motion.div
              className="who-we-are__deliver-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="who-we-are__deliver-header">
                <div className="who-we-are__deliver-icon">
                  <FaShieldAlt />
                </div>
                <h3>Data Engineering & AI Model Integration</h3>
              </div>
              <p>Clean data pipelines + powerful models = applications that make accurate, real-time decisions.</p>
            </motion.div>

            <motion.div
              className="who-we-are__deliver-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="who-we-are__deliver-header">
                <div className="who-we-are__deliver-icon">
                  <FaClock />
                </div>
                <h3>Rapid Prototyping & Agile Development</h3>
              </div>
              <p>Quick turnaround from concept to working prototype with iterative improvements based on feedback.</p>
            </motion.div>
          </div>
        </div>
      </section>
<section className="who-we-are__why-choose">
        <div className="who-we-are__why-choose-container">
          <motion.h2
            className="who-we-are__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why Choose App Developer Pro as a Full Stack Expert?
          </motion.h2>
          <p>Choosing the right app developer means choosing the future of your product. With deep expertise across AI, machine learning, frontend engineering, backend architecture, and cloud deployment, Full Stack Expert delivers solutions that are not just functional, but intelligently engineered to grow with your business.</p>
<p>As a development specialist, app developer pro focused on building applications that are fast, scalable, secure, and powered by real AI capabilities—from predictive analytics to automation and intelligent decision-making. Being a full stack expert, approach blends clean code, modern technology stacks, and user-centric design to ensure your application performs flawlessly in real-world conditions. Working with me means partnering with someone who is detail-oriented, innovation-driven, and committed to delivering measurable results. App Developer Pro doesn’t just build software—I create smart digital products that help businesses move faster, work smarter, and stay ahead of the competition.</p>
          <div className="who-we-are__why-choose-grid">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                className="who-we-are__why-choose-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="who-we-are__why-choose-header">
                  <div className="who-we-are__why-choose-icon">
                    <item.icon />
                  </div>
                  <h3>{item.title}</h3>
                </div>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="who-we-are__process">
        <div className="who-we-are__process-container">
          <motion.h2
            className="who-we-are__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            My Proven Web/Mobile Application & Software Development Process
          </motion.h2>
          <h3 className="who-we-are__process-subtitle">
            From Concept to Launch - A Systematic Approach to Excellence
          </h3>
          <p>Full Stack Expert follows a clear, structured, and result-driven web/mobile application & software development process that ensures every application App Developer builds apps & software fast, secure, and tailored to your business goals. From deep project discovery and wireframing to agile development, rigorous testing, and seamless deployment, every stage is designed to deliver maximum performance and user satisfaction. As a Full Stack Expert, App Developer Pro maintains transparent communication, provide real-time updates, and adapt quickly to your feedback—so your final product is not just functional, but truly exceptional. With a proven workflow that blends creativity, technology, and precision, Full Stack Specialist turns your ideas into powerful digital solutions that stand out in the market.</p>
          <div className="who-we-are__process-grid">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                className="who-we-are__process-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="who-we-are__process-step">{step.step}</div>
                <h4>{step.title}</h4>
                <p>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section className="who-we-are__values">
        <div className="who-we-are__values-container">
          <h2 className="who-we-are__section-title">
            Full Stack Expert Core Values
          </h2>
          <div className="who-we-are__values-grid">
          {values.map((value, index) => (
            <div
              key={value.title}
              className="who-we-are__value-card"
            >
              <div className="who-we-are__value-header">
                <div className="who-we-are__value-icon">
                  <value.icon />
                </div>
                <h3>{value.title}</h3>
              </div>
              <p>{value.description}</p>
            </div>
          ))}
          </div>
        </div>
      </section>

      <section className="who-we-are__team">
        <div className="who-we-are__team-container">
          <h2 className="who-we-are__section-title">
            Meet My Business Development Team
          </h2>
          <div className="who-we-are__team-grid">
          {team.map((member, index) => (
            <div
              key={member.name}
              className="who-we-are__team-card"
            >
              <img src={member.image} alt={member.name} />
              <h3>{member.name}</h3>
              <h4>{member.role}</h4>
              <p>{member.bio}</p>
            </div>
          ))}
          </div>
        </div>
      </section>

      <section className="who-we-are__stats">
        <div className="who-we-are__stats-container">
          <motion.h2
            className="who-we-are__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Proven Results in Web Development Excellence
          </motion.h2>
          <div className="who-we-are__stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="who-we-are__stat-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="who-we-are__stat-number">{stat.number}</h3>
                <h4 className="who-we-are__stat-label">{stat.label}</h4>
                <p className="who-we-are__stat-description">{stat.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      

      <section className="who-we-are__testimonials">
        <div className="who-we-are__testimonials-container">
          <motion.h2
            className="who-we-are__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Client Success Stories & Testimonials
          </motion.h2>
          <h3 className="who-we-are__testimonials-subtitle">
            What Our Clients Say About Our Web Development Services
          </h3>
          <div className="who-we-are__testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                className="who-we-are__testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="who-we-are__testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
                <p className="who-we-are__testimonial-content">"{testimonial.content}"</p>
                <div className="who-we-are__testimonial-author">
                  <h5>{testimonial.name}</h5>
                  <h6>{testimonial.role}, {testimonial.company}</h6>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      
    </div>
    </>
  );
};

export default AppsDevelopmentCompany;
